import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../../components/UI/Button';
import { ActionMenuItem } from '../../components/UI/ActionMenu';
import { ResponsiveEntityList } from '../../components/UI/ResponsiveEntityList';
import { Modal } from '../../components/UI/Modal';
import { useToast } from '../../components/UI/ToastProvider';
import { cannedResponseService } from '../../services/cannedResponseService';
import { CannedResponse } from '../../types/cannedResponse';

const emptyForm = {
  shortCode: '',
  title: '',
  content: '',
  category: '',
};

export const CannedResponsesPage: React.FC = () => {
  const { addToast } = useToast();
  const [responses, setResponses] = useState<CannedResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingResponse, setEditingResponse] = useState<CannedResponse | null>(null);
  const [responseToDelete, setResponseToDelete] = useState<CannedResponse | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadResponses = useCallback(async () => {
    setLoading(true);
    try
    {
      const data = await cannedResponseService.getAll(search || undefined);
      setResponses(data);
    }
    catch
    {
      addToast('Erro ao carregar respostas prontas', 'error');
    }
    finally
    {
      setLoading(false);
    }
  }, [addToast, search]);

  useEffect(() => {
    void loadResponses();
  }, [loadResponses]);

  const categories = useMemo(() => {
    const values = responses
      .map(r => r.category)
      .filter((c): c is string => Boolean(c && c.trim()));
    return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b));
  }, [responses]);

  const filtered = useMemo(() => {
    if (!categoryFilter)
    {
      return responses;
    }

    return responses.filter(r => r.category === categoryFilter);
  }, [responses, categoryFilter]);

  const openCreateModal = () => {
    setEditingResponse(null);
    setForm(emptyForm);
    setIsModalOpen(true);
  };

  const openEditModal = (item: CannedResponse) => {
    setEditingResponse(item);
    setForm({
      shortCode: item.shortCode,
      title: item.title,
      content: item.content,
      category: item.category ?? '',
    });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.shortCode.trim())
    {
      addToast('Informe o atalho (ex.: saudacao)', 'error');
      return;
    }

    if (!form.title.trim())
    {
      addToast('Informe o título', 'error');
      return;
    }

    if (!form.content.trim())
    {
      addToast('Informe o conteúdo da resposta', 'error');
      return;
    }

    setSaving(true);
    try
    {
      const payload = {
        shortCode: form.shortCode.trim(),
        title: form.title.trim(),
        content: form.content,
        category: form.category.trim() || null,
      };

      if (editingResponse)
      {
        await cannedResponseService.update(editingResponse.id, payload);
        addToast('Resposta pronta atualizada', 'success');
      }
      else
      {
        await cannedResponseService.create(payload);
        addToast('Resposta pronta criada', 'success');
      }

      setIsModalOpen(false);
      await loadResponses();
    }
    catch
    {
      addToast('Erro ao salvar resposta pronta', 'error');
    }
    finally
    {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!responseToDelete)
    {
      return;
    }

    try
    {
      await cannedResponseService.delete(responseToDelete.id);
      addToast('Resposta pronta removida', 'success');
      setIsDeleteModalOpen(false);
      setResponseToDelete(null);
      await loadResponses();
    }
    catch
    {
      addToast('Erro ao remover resposta pronta', 'error');
    }
  };

  const grouped = useMemo((): [string, CannedResponse[]][] => {
    const groups = new Map<string, CannedResponse[]>();

    filtered.forEach(item => {
      const key = item.category?.trim() || 'Sem categoria';
      const list = groups.get(key) ?? [];
      list.push(item);
      groups.set(key, list);
    });

    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Respostas prontas</h1>
          <p className="text-gray-600 text-sm mt-1">
            Atalhos para mensagens recorrentes — use <code className="text-xs bg-gray-100 px-1 rounded">/atalho</code> no composer
          </p>
        </div>
        <Button variant="primary" onClick={openCreateModal}>
          Nova resposta
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-6 flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          placeholder="Buscar por atalho, título ou conteúdo..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
        />
        <select
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-2 text-sm sm:w-48"
        >
          <option value="">Todas as categorias</option>
          {categories.map(category => (
            <option key={category} value={category}>{category}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-500">Nenhuma resposta pronta cadastrada.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map(([category, items]) => (
            <div key={category} className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-4 py-3 md:px-6 bg-gray-50 border-b">
                <h2 className="text-sm font-semibold text-gray-700">{category}</h2>
              </div>
              <div className="p-3 md:p-0 md:bg-transparent">
                <ResponsiveEntityList
                  items={items}
                  getKey={(item) => item.id}
                  embedded
                  mobileTitle={(item) => item.title}
                  mobileFields={(item) => [
                    { label: 'Atalho', value: `/${item.shortCode}` },
                    { label: 'Prévia', value: <span className="line-clamp-2">{item.content}</span> },
                  ]}
                  columns={[
                    {
                      header: 'Atalho',
                      cellClassName: 'px-6 py-4 text-sm font-mono text-blue-700',
                      cell: (item) => `/${item.shortCode}`,
                    },
                    {
                      header: 'Título',
                      cellClassName: 'px-6 py-4 text-sm text-gray-900',
                      cell: (item) => item.title,
                    },
                    {
                      header: 'Prévia',
                      cellClassName: 'px-6 py-4 text-sm text-gray-600 max-w-md truncate',
                      cell: (item) => item.content,
                    },
                  ]}
                  actions={(item): ActionMenuItem[] => [
                    {
                      key: 'edit',
                      label: 'Editar',
                      onClick: () => openEditModal(item),
                      icon: (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      ),
                    },
                    {
                      key: 'delete',
                      label: 'Remover',
                      variant: 'danger',
                      onClick: () => {
                        setResponseToDelete(item);
                        setIsDeleteModalOpen(true);
                      },
                      icon: (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      ),
                    },
                  ]}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingResponse ? 'Editar resposta pronta' : 'Nova resposta pronta'}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Atalho</label>
            <div className="flex items-center gap-1">
              <span className="text-gray-500 text-sm">/</span>
              <input
                type="text"
                value={form.shortCode}
                onChange={e => setForm(f => ({ ...f, shortCode: e.target.value.replace(/\s/g, '') }))}
                placeholder="saudacao"
                className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm font-mono"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Título</label>
            <input
              type="text"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Categoria (opcional)</label>
            <input
              type="text"
              value={form.category}
              onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
              placeholder="Ex.: Saudação, Encerramento"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Conteúdo</label>
            <textarea
              value={form.content}
              onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              rows={5}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>Cancelar</Button>
            <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Remover resposta pronta"
      >
        <p className="text-sm text-gray-600 mb-4">
          Deseja remover a resposta <strong>/{responseToDelete?.shortCode}</strong>?
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)}>Cancelar</Button>
          <Button variant="danger" onClick={() => void handleDelete()}>Remover</Button>
        </div>
      </Modal>
    </div>
  );
};
